<script setup>
import { ref } from 'vue';
import { useAuth } from '../composables/useAuth.js';
import { REQUEST_LIMITS } from '../domain/accessRequest.js';
import PasswordField from './PasswordField.vue';

const { signIn, requestAccess, requestPasswordReset, linkProblem } = useAuth();

const mail = ref('');
const password = ref('');
const busy = ref(false);
const error = ref(null);

const mode = ref('signIn'); // signIn | request | forgot
const reqFirstName = ref('');
const reqLastName = ref('');
const reqOrganization = ref('');
const reqMail = ref('');
const reqDone = ref(false);

const forgotMail = ref('');
const forgotDone = ref(false);

async function submit() {
  busy.value = true;
  error.value = null;
  try {
    await signIn(mail.value, password.value);
  } catch (e) {
    error.value = e.message === 'genericError' ? 'auth.genericError' : `errors.${e.message}`;
  } finally {
    busy.value = false;
  }
}

async function submitRequest() {
  busy.value = true;
  error.value = null;
  try {
    await requestAccess({
      firstName: reqFirstName.value,
      lastName: reqLastName.value,
      organization: reqOrganization.value,
      email: reqMail.value,
    });
    reqDone.value = true;
  } catch (e) {
    error.value = `errors.${e.message}`;
  } finally {
    busy.value = false;
  }
}

async function submitForgot() {
  busy.value = true;
  error.value = null;
  try {
    await requestPasswordReset(forgotMail.value);
    forgotDone.value = true;
  } catch (e) {
    /* requestPasswordReset wirft nur bei echten Verbindungsproblemen und beim Mail-Limit --
       ob die Adresse existiert, wird nie sichtbar. */
    error.value = `errors.${e.message}`;
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <div class="signin">
    <form v-if="mode === 'signIn'" @submit.prevent="submit">
      <p v-if="linkProblem" class="msg error" role="alert" data-testid="link-problem">
        {{ $t(linkProblem === 'expired' ? 'auth.linkExpired' : 'auth.linkInvalid') }}
      </p>
      <label class="field">
        <span>{{ $t('auth.email') }}</span>
        <input v-model="mail" type="email" autocomplete="username" required />
      </label>
      <label class="field">
        <span>{{ $t('auth.password') }}</span>
        <PasswordField v-model="password" autocomplete="current-password" required />
      </label>

      <p v-if="error" class="msg error">{{ $t(error) }}</p>

      <button type="submit" class="primary" :disabled="busy">
        {{ busy ? $t('common.loading') : $t('auth.signIn') }}
      </button>
      <button type="button" class="link" @click="mode = 'forgot'; error = null">
        {{ $t('auth.forgotPassword') }}
      </button>
      <button type="button" class="link" @click="mode = 'request'">
        {{ $t('auth.requestAccess') }}
      </button>
    </form>

    <div v-else-if="mode === 'forgot'">
      <p v-if="forgotDone" class="msg ok">{{ $t('auth.forgotSent') }}</p>
      <form v-else @submit.prevent="submitForgot">
        <label class="field">
          <span>{{ $t('auth.email') }}</span>
          <input v-model="forgotMail" type="email" autocomplete="username" required />
        </label>

        <p v-if="error" class="msg error">{{ $t(error) }}</p>

        <button type="submit" class="primary" :disabled="busy">
          {{ busy ? $t('common.loading') : $t('auth.forgotSubmit') }}
        </button>
      </form>
      <button type="button" class="link" @click="mode = 'signIn'; error = null">
        {{ $t('common.back') }}
      </button>
    </div>

    <div v-else>
      <p v-if="reqDone" class="msg ok">{{ $t('auth.requestSent') }}</p>
      <form v-else @submit.prevent="submitRequest">
        <label class="field">
          <span>{{ $t('auth.firstName') }}</span>
          <input
            v-model="reqFirstName"
            type="text"
            autocomplete="given-name"
            :maxlength="REQUEST_LIMITS.firstName"
            required
          />
        </label>
        <label class="field">
          <span>{{ $t('auth.lastName') }}</span>
          <input
            v-model="reqLastName"
            type="text"
            autocomplete="family-name"
            :maxlength="REQUEST_LIMITS.lastName"
            required
          />
        </label>
        <label class="field">
          <span>{{ $t('auth.organization') }}</span>
          <input
            v-model="reqOrganization"
            type="text"
            autocomplete="organization"
            :placeholder="$t('auth.organizationPlaceholder')"
            :maxlength="REQUEST_LIMITS.organization"
            required
          />
        </label>
        <label class="field">
          <span>{{ $t('auth.email') }}</span>
          <input
            v-model="reqMail"
            type="email"
            autocomplete="email"
            :maxlength="REQUEST_LIMITS.email"
            required
          />
        </label>

        <p v-if="error" class="msg error">{{ $t(error) }}</p>

        <button type="submit" class="primary" :disabled="busy">
          {{ busy ? $t('common.loading') : $t('auth.requestAccess') }}
        </button>
        <p class="hint">{{ $t('auth.requestHint') }}</p>
      </form>
      <button type="button" class="link" @click="mode = 'signIn'">
        {{ $t('common.back') }}
      </button>
    </div>

    <!-- Vertraulichkeitshinweis: in allen drei Ansichten des Login-Bildschirms, nicht im Orbit-Kopf -->
    <p class="confidential" data-testid="confidential">{{ $t('app.confidential') }}</p>
  </div>
</template>

<style scoped>
.signin {
  max-width: 340px;
  margin: 12px auto 0;
  padding: 26px 24px;
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 14px;
}
.field {
  display: block;
  margin-bottom: 14px;
}
.field span {
  display: block;
  font-size: 0.7rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--dim);
  margin-bottom: 6px;
}
.field :deep(input) {
  width: 100%;
  padding: 10px 12px;
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: 9px;
  color: var(--text);
  font-family: var(--sans);
  font-size: 0.88rem;
  outline: none;
  transition: border-color 0.18s;
}
.field :deep(input:focus) {
  border-color: var(--green-dim);
}
.primary {
  width: 100%;
  padding: 11px;
  margin-top: 4px;
  background: var(--green);
  border: 0;
  border-radius: 9px;
  color: rgb(var(--v-theme-on-primary));
  font-family: var(--sans);
  font-size: 0.86rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.18s;
}
.primary:disabled {
  opacity: 0.6;
  cursor: default;
}
.link {
  width: 100%;
  margin-top: 12px;
  padding: 4px;
  background: none;
  border: 0;
  color: var(--muted);
  font-family: var(--sans);
  font-size: 0.78rem;
  cursor: pointer;
}
.link:hover {
  color: var(--green);
}
.hint {
  margin: 10px 0 0;
  font-size: 0.72rem;
  line-height: 1.5;
  color: var(--dim);
}
.confidential {
  margin: 18px 0 0;
  padding-top: 14px;
  border-top: 1px solid var(--border);
  font-size: 0.7rem;
  line-height: 1.5;
  text-align: center;
  color: var(--dim);
  text-wrap: balance;
}
.msg {
  font-size: 0.78rem;
  line-height: 1.5;
  margin-bottom: 12px;
}
.msg.error {
  color: var(--red);
}
.msg.ok {
  color: var(--green);
}
</style>
